import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { logError } from '../lib/errorLogger';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: -20 };
const AXIS_TICK = { fontSize: 11, fill: '#8C7A76' };
const Y_DOMAIN = [0, 10];

const METRICS = [
  { key: 'acne_severity', label: 'Acne', color: '#A388EE' },
  { key: 'bloating_level', label: 'Bloating', color: '#4ADE80' },
  { key: 'stress_level', label: 'Stress', color: '#FF6B4A' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-lg p-3 border border-[#E5D0C9]">
      <p className="text-xs font-medium text-[#1A1514] mb-1.5">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[#8C7A76]">{entry.name}:</span>
          <span className="font-medium text-[#1A1514]">{entry.value}/10</span>
        </div>
      ))}
    </div>
  );
};

const TrendBadge = ({ data, metricKey }) => {
  if (data.length < 3) return null;
  const recent = data.slice(-3);
  const older = data.slice(0, Math.max(1, data.length - 3));
  const recentAvg = recent.reduce((s, d) => s + (d[metricKey] || 0), 0) / recent.length;
  const olderAvg = older.reduce((s, d) => s + (d[metricKey] || 0), 0) / older.length;
  const diff = recentAvg - olderAvg;

  if (Math.abs(diff) < 0.5) {
    return <span className="flex items-center gap-1 text-[10px] text-[#8C7A76]"><Minus className="w-3 h-3" /> Stable</span>;
  }
  // Lower is better for acne/bloating/stress
  if (diff < 0) {
    return <span className="flex items-center gap-1 text-[10px] text-[#4ADE80]"><TrendingDown className="w-3 h-3" /> Improving</span>;
  }
  return <span className="flex items-center gap-1 text-[10px] text-[#A388EE]"><TrendingUp className="w-3 h-3" /> Rising</span>;
};

const ChartSummary = ({ chartData }) => (
  <div className="mt-3 grid grid-cols-3 gap-2">
    {METRICS.map((m) => {
      const latest = chartData[chartData.length - 1]?.[m.key] || 0;
      return (
        <div key={m.key} className="text-center p-2 bg-[#FFF2EC] rounded-lg">
          <p className="text-lg font-bold" style={{ color: m.color }}>{latest}</p>
          <p className="text-[10px] text-[#8C7A76]">{m.label} today</p>
        </div>
      );
    })}
  </div>
);

const MetricToggles = ({ activeMetrics, toggleMetric, chartData }) => (
  <div className="flex flex-wrap gap-2 mb-4">
    {METRICS.map((m) => (
      <button
        key={m.key}
        onClick={() => toggleMetric(m.key)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
          activeMetrics.includes(m.key)
            ? 'text-white'
            : 'bg-[#FFF2EC] text-[#8C7A76] hover:bg-[#E5D0C9]'
        }`}
        style={activeMetrics.includes(m.key) ? { backgroundColor: m.color } : {}}
        data-testid={`toggle-${m.key}`}
      >
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: activeMetrics.includes(m.key) ? '#fff' : m.color }} />
        {m.label}
        <TrendBadge data={chartData} metricKey={m.key} />
      </button>
    ))}
  </div>
);

const formatLogHistory = (data) => {
  const sorted = (data || []).sort((a, b) => a.date.localeCompare(b.date));
  return sorted.map((log) => ({
    date: new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    acne_severity: log.acne_severity || 0,
    bloating_level: log.bloating_level || 0,
    stress_level: log.stress_level || 0,
    water_glasses: log.water_glasses || 0,
    sleep_hours: log.sleep_hours || 0,
  }));
};

const AcneTrendChart = () => {
  const [chartData, setChartData] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState(['acne_severity', 'bloating_level', 'stress_level']);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logs/history?days=30`, { withCredentials: true });
      setChartData(formatLogHistory(res.data));
    } catch (err) {
      logError('AcneTrendChart', 'Failed to fetch log history', { error: err?.message });
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const toggleMetric = (key) => {
    setActiveMetrics((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const activeMetricData = useMemo(() =>
    METRICS.filter((m) => activeMetrics.includes(m.key)),
  [activeMetrics]);

  if (chartData.length < 2) {
    return (
      <Card className="card-elevated border-0" data-testid="acne-trend-chart">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-[#8C7A76]">Log at least 2 days to see your trend chart.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elevated border-0" data-testid="acne-trend-chart">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[#1A1514] text-base">Skin Health Trends</CardTitle>
          <span className="text-xs text-[#8C7A76]">{chartData.length} days</span>
        </div>
      </CardHeader>
      <CardContent>
        <MetricToggles activeMetrics={activeMetrics} toggleMetric={toggleMetric} chartData={chartData} />
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={CHART_MARGIN}>
              <defs>
                {METRICS.map((m) => (
                  <linearGradient key={`grad-${m.key}`} id={`gradient-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={m.color} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5D0C9" vertical={false} />
              <XAxis dataKey="date" tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <YAxis domain={Y_DOMAIN} tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              {activeMetricData.map((m) => (
                <Area key={m.key} type="monotone" dataKey={m.key} stroke={m.color} fill={`url(#gradient-${m.key})`} strokeWidth={2} name={m.label} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <ChartSummary chartData={chartData} />
      </CardContent>
    </Card>
  );
};

export default AcneTrendChart;
